import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "../../api/http";
import { getStoredSession, loginWithCode, logoutLocally, sendLoginCode } from "../../api/auth";
import {
  getTeacherDashboardStudentDetail,
  getTeacherDashboardStudents,
  getTeacherDashboardSummary,
  TeacherDashboardLink,
  TeacherDashboardStudent,
  TeacherDashboardStudentDetail,
  TeacherDashboardStudents,
  TeacherDashboardSummary,
} from "../../api/teacherWeb";
import { AuthSession } from "../../api/tokenStore";

type LoadState = "idle" | "loading" | "ready" | "error";
type LoginState = "idle" | "sending" | "loggingIn";

const techTypeLabels: Record<string, string> = {
  left_backhand_backspin: "左手反手拉下旋球",
  left_backhand_chop: "左手反手削球",
  left_backhand_defense: "左手反手防守",
  left_backhand_drive: "左手反手攻球",
  left_backhand_push: "左手反手搓球",
  left_backhand_topspin: "左手反手拉上旋球",
  left_forehand_backspin: "左手正手拉下旋球",
  left_forehand_chop: "左手正手削球",
  left_forehand_defense: "左手正手防守",
  left_forehand_drive: "左手正手攻球",
  left_forehand_push: "左手正手搓球",
  left_forehand_topspin: "左手正手拉上旋球",
  right_backhand_backspin: "右手反手拉下旋球",
  right_backhand_chop: "右手反手削球",
  right_backhand_defense: "右手反手防守",
  right_backhand_drive: "右手反手攻球",
  right_backhand_push: "右手反手搓球",
  right_backhand_topspin: "右手反手拉上旋球",
  right_forehand_backspin: "右手正手拉下旋球",
  right_forehand_chop: "右手正手削球",
  right_forehand_defense: "右手正手防守",
  right_forehand_drive: "右手正手攻球",
  right_forehand_push: "右手正手搓球",
  right_forehand_topspin: "右手正手拉上旋球",
};

const fallbackActionKeys = [
  "right_forehand_drive",
  "right_forehand_backspin",
  "right_forehand_push",
  "right_backhand_chop",
  "right_backhand_defense",
];

const demoErrorRows = [
  { label: "重心过高", count: 35 },
  { label: "抬肘过多", count: 12 },
  { label: "转腰不足", count: 7 },
  { label: "引拍较小", count: 5 },
  { label: "夹臂", count: 1 },
];

type ActionItem = {
  key: string;
  label: string;
  value: number;
};

function humanError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "操作失败，请稍后重试";
}

function isPhoneInputValid(phone: string): boolean {
  return /^\d{11}$/.test(phone.trim());
}

function isCodeInputValid(code: string): boolean {
  return /^\d{6}$/.test(code.trim());
}

function formatDateTime(value?: string | null): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("zh-CN", {
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    month: "2-digit",
  });
}

function formatQuota(value: number): string {
  return value === -1 ? "不限" : value.toLocaleString("zh-CN");
}

function techLabel(value?: string | null): string {
  if (!value) return "-";
  return techTypeLabels[value] ?? value;
}

function statusLabel(value?: string | null): string {
  switch (value) {
    case "comp completed":
      return "已完成";
    case "comp failed":
      return "失败";
    case "started":
      return "分析中";
    default:
      return value || "-";
  }
}

function statusClass(value?: string | null): string {
  if (value === "comp completed") return "success";
  if (value === "comp failed") return "error";
  return "running";
}

function displayStudentName(student: TeacherDashboardStudent | TeacherDashboardStudentDetail): string {
  return student.studentName?.trim() || "未填写姓名";
}

function topActionItems(actionCounts: Record<string, number>): ActionItem[] {
  const realItems = Object.entries(actionCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([key, value]) => ({ key, label: techLabel(key), value }));

  if (realItems.length > 0) {
    return realItems;
  }

  return fallbackActionKeys.map((key, index) => ({
    key,
    label: techLabel(key),
    value: [18, 14, 11, 9, 7][index] ?? 5,
  }));
}

function MetricCard({
  label,
  sublabel,
  tone = "default",
  value,
}: {
  label: string;
  sublabel?: string;
  tone?: "default" | "blue" | "green" | "amber";
  value: string;
}) {
  return (
    <article className={`dashboard-metric ${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      {sublabel ? <small>{sublabel}</small> : null}
    </article>
  );
}

function LinkTabs({
  links,
  selectedLinkId,
  onSelect,
}: {
  links: TeacherDashboardLink[];
  selectedLinkId: number | null;
  onSelect: (value: number | null) => void;
}) {
  return (
    <div className="dashboard-tabs compact-scroll" role="tablist" aria-label="班级筛选">
      <button className={selectedLinkId === null ? "active" : ""} type="button" onClick={() => onSelect(null)}>
        全部班级
      </button>
      {links.map((link) => (
        <button
          className={selectedLinkId === link.id ? "active" : ""}
          key={link.id}
          type="button"
          onClick={() => onSelect(link.id)}
        >
          {link.title || `链接 ${link.id}`}
        </button>
      ))}
    </div>
  );
}

function DashboardHeader({
  rangeText,
  onLogout,
}: {
  rangeText: string;
  onLogout: () => void;
}) {
  return (
    <header className="teacher-board-top">
      <div className="teacher-board-brand">
        <img src="/pingpi-logo.png" alt="" aria-hidden="true" />
        <span>迹时运动</span>
      </div>
      <button className="teacher-board-logout" type="button" onClick={onLogout} aria-label="退出登录">
        <span aria-hidden="true">退出</span>
      </button>
      <div className="teacher-board-title">
        <p>教师看板</p>
        <h2>{rangeText}</h2>
      </div>
    </header>
  );
}

function ActionDistribution({
  actionCounts,
  onSelect,
  selectedTech,
}: {
  actionCounts: Record<string, number>;
  onSelect: (tech: string) => void;
  selectedTech: string;
}) {
  const items = useMemo(() => {
    const actions = topActionItems(actionCounts);
    const max = Math.max(1, ...actions.map((item) => item.value));
    return actions.map((item) => ({
      ...item,
      ratio: Math.max(8, Math.round((item.value / max) * 100)),
    }));
  }, [actionCounts]);

  return (
    <div className="action-bars">
      {items.map((item) => (
        <button
          className={`action-bar-row ${selectedTech === item.key ? "active" : ""}`}
          key={item.key}
          type="button"
          onClick={() => onSelect(item.key)}
        >
          <div>
            <strong>{item.label}</strong>
            <span>{item.value} 次</span>
          </div>
          <i><b style={{ width: `${item.ratio}%` }} /></i>
        </button>
      ))}
    </div>
  );
}

function ErrorDistribution({
  actionOptions,
  onSelect,
  selectedTech,
}: {
  actionOptions: ActionItem[];
  onSelect: (tech: string) => void;
  selectedTech: string;
}) {
  const max = Math.max(1, ...demoErrorRows.map((item) => item.count));

  return (
    <div className="error-distribution">
      <div className="error-distribution-head">
        <div>
          <p className="section-label">错误</p>
          <h3>错误分布</h3>
          <label className="error-tech-select">
            <select value={selectedTech} onChange={(event) => onSelect(event.target.value)}>
              {actionOptions.map((item) => (
                <option key={item.key} value={item.key}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <span className="error-demo-badge">示例</span>
      </div>
      <div className="error-list">
        {demoErrorRows.map((error) => (
          <div className="error-row" key={error.label}>
            <strong>{error.label}</strong>
            <small>{error.count} 人</small>
            <i><b style={{ width: `${Math.max(8, Math.round((error.count / max) * 100))}%` }} /></i>
          </div>
        ))}
      </div>
    </div>
  );
}

function StudentList({
  onSelect,
  selectedKey,
  students,
}: {
  onSelect: (student: TeacherDashboardStudent) => void;
  selectedKey: string;
  students: TeacherDashboardStudent[];
}) {
  if (students.length === 0) {
    return <p className="dashboard-empty">当前班级还没有学生绑定。</p>;
  }

  return (
    <div className="student-card-list">
      {students.map((student) => {
        const rowKey = `${student.linkId}:${student.studentUserId}`;
        const hasLatestResult = student.latestRunStatus === "comp completed" && Boolean(student.latestRunId);
        return (
          <article
            className={`student-card-row ${rowKey === selectedKey ? "active" : ""}`}
            key={rowKey}
          >
            <button className="student-card-content" type="button" onClick={() => onSelect(student)}>
              <div className="student-card-main">
                <div>
                  <strong>{displayStudentName(student)}</strong>
                  <span>{student.phoneMasked}</span>
                </div>
                <span className={`status-pill ${statusClass(student.latestRunStatus)}`}>
                  {statusLabel(student.latestRunStatus)}
                </span>
              </div>
              <div className="student-card-meta">
                <span>班级：{student.linkTitle || `链接 ${student.linkId}`}</span>
                <span>共 {student.runCount} 次</span>
                <span>最近：{techLabel(student.latestTechType)}</span>
              </div>
            </button>
            <div className="student-card-footer">
              <button className="student-card-detail-button" type="button" onClick={() => onSelect(student)}>
                查看记录
              </button>
              {hasLatestResult ? (
                <Link className="student-card-result-link" to={`/r/${student.latestRunId}`}>
                  最近结果
                </Link>
              ) : null}
            </div>
          </article>
        );
      })}
    </div>
  );
}

function StudentDetailCard({ detail }: { detail: TeacherDashboardStudentDetail | null }) {
  if (!detail) {
    return (
      <article className="dashboard-card student-detail-card">
        <p className="section-label">学生</p>
        <h3>学生详情</h3>
        <p className="dashboard-empty">点击左侧学生查看历史分析。</p>
      </article>
    );
  }

  return (
    <article className="dashboard-card student-detail-card">
      <p className="section-label">学生</p>
      <div className="detail-title-row">
        <div>
          <h3>{displayStudentName(detail)}</h3>
          <span>{detail.phoneMasked}</span>
        </div>
        <strong>{detail.runs.length} 次</strong>
      </div>
      <div className="detail-bindings">
        {detail.bindings.map((binding) => (
          <span key={binding.linkId}>{binding.linkTitle || `链接 ${binding.linkId}`}</span>
        ))}
      </div>
      <div className="detail-run-list">
        {detail.runs.length === 0 ? <p className="dashboard-empty">该学生还没有网页端 AI教练记录。</p> : null}
        {detail.runs.map((run) => (
          <div className="detail-run-item" key={run.runId}>
            <div>
              <strong>{techLabel(run.techType)}</strong>
              <span>{formatDateTime(run.createdAt)} · {run.linkTitle || `链接 ${run.linkId}`}</span>
            </div>
            <div className="detail-run-actions">
              <span className={`status-pill ${statusClass(run.status)}`}>{statusLabel(run.status)}</span>
              {run.status === "comp completed" ? <Link to={`/r/${run.runId}`}>结果</Link> : null}
            </div>
          </div>
        ))}
      </div>
    </article>
  );
}

function TeacherLoginCard({
  code,
  cooldown,
  error,
  loginState,
  onCodeChange,
  onLogin,
  onPhoneChange,
  onSendCode,
  phone,
}: {
  code: string;
  cooldown: number;
  error: string;
  loginState: LoginState;
  onCodeChange: (value: string) => void;
  onLogin: (event: FormEvent<HTMLFormElement>) => void;
  onPhoneChange: (value: string) => void;
  onSendCode: () => void;
  phone: string;
}) {
  const canSendCode = isPhoneInputValid(phone) && loginState === "idle" && cooldown <= 0;
  const canSubmitLogin = isPhoneInputValid(phone) && isCodeInputValid(code) && loginState === "idle";

  return (
    <section className="student-login-page teacher-login-page">
      <article className="student-login-card teacher-login-card">
        <div className="student-login-header">
          <div className="student-login-brand" aria-label="迹时运动">
            <img src="/pingpi-logo.png" alt="" aria-hidden="true" />
            <span>迹时运动</span>
          </div>
          <span className="student-login-mark">教师看板</span>
        </div>

        <div className="student-login-copy teacher-login-copy">
          <h2>老师登录</h2>
          <p>登录后查看班级使用、学生记录和 AI教练结果。</p>
        </div>

        <form className="student-login-form teacher-login-form" onSubmit={onLogin}>
          <label className="student-login-field">
            <span className="student-login-prefix">+86</span>
            <input
              aria-label="手机号"
              inputMode="numeric"
              maxLength={11}
              onChange={(event) => onPhoneChange(event.target.value.replace(/\D/g, "").slice(0, 11))}
              placeholder="11 位手机号"
              value={phone}
            />
          </label>
          <label className="student-login-field">
            <input
              aria-label="验证码"
              inputMode="numeric"
              maxLength={6}
              onChange={(event) => onCodeChange(event.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="6 位验证码"
              value={code}
            />
            <button className="student-login-code" disabled={!canSendCode} type="button" onClick={onSendCode}>
              {cooldown > 0 ? `已发送(${cooldown}s)` : loginState === "sending" ? "发送中" : "获取验证码"}
            </button>
          </label>
          {error ? <p className="form-error">{error}</p> : null}
          <button className="primary-button student-login-submit" disabled={!canSubmitLogin} type="submit">
            {loginState === "loggingIn" ? "登录中" : "进入看板"}
          </button>
        </form>
      </article>
    </section>
  );
}

export function TeacherDashboardPage() {
  const studentDetailRef = useRef<HTMLDivElement | null>(null);
  const [session, setSession] = useState<AuthSession | null>(() => getStoredSession());
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [loginState, setLoginState] = useState<LoginState>("idle");
  const [loginError, setLoginError] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [loadState, setLoadState] = useState<LoadState>("idle");
  const [loadError, setLoadError] = useState("");
  const [loadErrorCode, setLoadErrorCode] = useState("");
  const [summary, setSummary] = useState<TeacherDashboardSummary | null>(null);
  const [studentsData, setStudentsData] = useState<TeacherDashboardStudents | null>(null);
  const [selectedLinkId, setSelectedLinkId] = useState<number | null>(null);
  const [selectedStudentKey, setSelectedStudentKey] = useState("");
  const [studentDetail, setStudentDetail] = useState<TeacherDashboardStudentDetail | null>(null);
  const [selectedErrorTech, setSelectedErrorTech] = useState(fallbackActionKeys[0]);

  const students = studentsData?.students ?? [];
  const selectedLink = summary?.links.find((link) => link.id === selectedLinkId) ?? null;
  const teacherName = summary?.teacherName?.trim() || "未配置教师姓名";
  const rangeText = summary ? `${teacherName} · ${selectedLink?.title || "全部班级"}` : "正在读取教师网页端数据";
  const errorActionOptions = useMemo(
    () => topActionItems(summary?.actionCounts ?? {}),
    [summary?.actionCounts],
  );

  const loadDashboard = useCallback((linkId: number | null, signal?: AbortSignal) => {
    setLoadState("loading");
    setLoadError("");
    setLoadErrorCode("");

    Promise.all([
      getTeacherDashboardSummary(linkId, signal),
      getTeacherDashboardStudents(linkId, signal),
    ])
      .then(([summaryData, studentsResult]) => {
        setSummary(summaryData);
        setStudentsData(studentsResult);
        setLoadState("ready");

        const firstStudent = studentsResult.students[0];
        if (firstStudent) {
          const nextKey = `${firstStudent.linkId}:${firstStudent.studentUserId}`;
          setSelectedStudentKey(nextKey);
          return getTeacherDashboardStudentDetail(firstStudent.studentUserId, firstStudent.linkId, signal)
            .then(setStudentDetail)
            .catch(() => setStudentDetail(null));
        }

        setSelectedStudentKey("");
        setStudentDetail(null);
      })
      .catch((error: unknown) => {
        if (signal?.aborted) return;
        setLoadState("error");
        setLoadErrorCode(error instanceof ApiError ? error.code ?? "" : "");
        setLoadError(humanError(error));
      });
  }, []);

  useEffect(() => {
    if (!session) return;
    const controller = new AbortController();
    loadDashboard(selectedLinkId, controller.signal);
    return () => controller.abort();
  }, [loadDashboard, selectedLinkId, session]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setInterval(() => setCooldown((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  useEffect(() => {
    if (!summary) return;
    const actions = topActionItems(summary.actionCounts);
    if (!actions.some((item) => item.key === selectedErrorTech)) {
      setSelectedErrorTech(actions[0]?.key ?? fallbackActionKeys[0]);
    }
  }, [selectedErrorTech, summary]);

  async function handleSendCode() {
    if (!isPhoneInputValid(phone)) {
      setLoginError("请输入 11 位手机号");
      return;
    }

    setLoginError("");
    setLoginState("sending");
    try {
      const response = await sendLoginCode(phone.trim());
      setCooldown(response.cooldownSec || 60);
    } catch (error) {
      const message = humanError(error);
      setLoginError(error instanceof ApiError && error.retryAfterSec ? `${message}，${error.retryAfterSec} 秒后再试` : message);
    } finally {
      setLoginState("idle");
    }
  }

  async function handleLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!isPhoneInputValid(phone)) {
      setLoginError("请输入 11 位手机号");
      return;
    }
    if (!isCodeInputValid(code)) {
      setLoginError("请输入 6 位验证码");
      return;
    }

    setLoginError("");
    setLoginState("loggingIn");
    try {
      const nextSession = await loginWithCode(phone.trim(), code.trim());
      setSession(nextSession);
    } catch (error) {
      setLoginError(humanError(error));
    } finally {
      setLoginState("idle");
    }
  }

  function handleLogout() {
    logoutLocally();
    setSession(null);
    setSummary(null);
    setStudentsData(null);
    setStudentDetail(null);
    setLoadError("");
    setLoadErrorCode("");
  }

  function handleStudentSelect(student: TeacherDashboardStudent) {
    const nextKey = `${student.linkId}:${student.studentUserId}`;
    setSelectedStudentKey(nextKey);
    window.requestAnimationFrame(() => {
      studentDetailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    getTeacherDashboardStudentDetail(student.studentUserId, student.linkId)
      .then(setStudentDetail)
      .catch((error: unknown) => setLoadError(humanError(error)));
  }

  if (!session) {
    return (
      <TeacherLoginCard
        code={code}
        cooldown={cooldown}
        error={loginError}
        loginState={loginState}
        onCodeChange={setCode}
        onLogin={handleLogin}
        onPhoneChange={setPhone}
        onSendCode={handleSendCode}
        phone={phone}
      />
    );
  }

  const isTeacherAccessPending = loadState === "error" && loadErrorCode === "TEACHER_ACCESS_REQUIRED";

  if (isTeacherAccessPending) {
    return (
      <section className="student-login-page teacher-login-page">
        <article className="student-login-card teacher-login-card">
          <div className="student-login-header">
            <div className="student-login-brand" aria-label="迹时运动">
              <img src="/pingpi-logo.png" alt="" aria-hidden="true" />
              <span>迹时运动</span>
            </div>
            <span className="student-login-mark">教师看板</span>
          </div>

          <div className="teacher-access-card">
            <div>
              <p className="section-label">等待开通</p>
              <h3>账号已创建</h3>
              <p>请联系管理员开通教师权限。</p>
              <p>开通后刷新本页，即可进入教师看板。</p>
            </div>
            <div className="teacher-access-actions">
              <button className="primary-button" type="button" onClick={() => loadDashboard(selectedLinkId)}>
                重新检查
              </button>
              <button className="ghost-button" type="button" onClick={handleLogout}>
                退出登录
              </button>
            </div>
          </div>
        </article>
      </section>
    );
  }

  return (
    <section className="teacher-dashboard-page">
      <DashboardHeader rangeText={rangeText} onLogout={handleLogout} />

      {loadState === "error" && loadErrorCode !== "TEACHER_ACCESS_REQUIRED" ? (
        <article className="dashboard-card">
          <div className="dashboard-error">
            <strong>{loadError || "看板读取失败"}</strong>
            <button className="ghost-button" type="button" onClick={() => loadDashboard(selectedLinkId)}>
              重试
            </button>
          </div>
        </article>
      ) : null}

      {summary ? (
        <>
          <LinkTabs links={summary.links} selectedLinkId={selectedLinkId} onSelect={setSelectedLinkId} />
          <div className="dashboard-metrics">
            <MetricCard label="学生绑定" sublabel={`${summary.uniqueStudentCount} 个唯一学生`} value={String(summary.studentBindingCount)} tone="blue" />
            <MetricCard label="AI教练使用" sublabel={`本月 ${summary.monthRuns} 次`} value={String(summary.totalRuns)} />
            <MetricCard label="完成分析" sublabel={`失败 ${summary.failedRuns} 次`} value={String(summary.completedRuns)} tone="green" />
            <MetricCard
              label="剩余额度"
              sublabel={`已用 ${formatQuota(summary.quota.usedCount)} 次`}
              value={formatQuota(summary.quota.remainingCount)}
              tone="amber"
            />
          </div>

          <div className="dashboard-insight-grid">
            <article className="dashboard-card">
              <div className="dashboard-card-title">
                <div>
                  <p className="section-label">动作</p>
                  <h3>动作分布</h3>
                </div>
                <span>{selectedLink?.title || "全部班级"}</span>
              </div>
              <ActionDistribution
                actionCounts={summary.actionCounts}
                selectedTech={selectedErrorTech}
                onSelect={setSelectedErrorTech}
              />
            </article>

            <article className="dashboard-card">
              <ErrorDistribution
                actionOptions={errorActionOptions}
                selectedTech={selectedErrorTech}
                onSelect={setSelectedErrorTech}
              />
            </article>
          </div>

          <div className="dashboard-secondary-grid">
            <article className="dashboard-card">
              <div className="dashboard-card-title">
                <div>
                  <p className="section-label">学生</p>
                  <h3>学生列表</h3>
                </div>
                <span>{studentsData?.total ?? 0} 条</span>
              </div>
              {loadState === "loading" ? <p className="dashboard-empty">正在读取学生数据...</p> : null}
              <StudentList students={students} selectedKey={selectedStudentKey} onSelect={handleStudentSelect} />
            </article>

            <article className="dashboard-card">
              <div className="dashboard-card-title">
                <div>
                  <p className="section-label">链接</p>
                  <h3>班级链接</h3>
                </div>
                <span>{summary.activeLinkCount}/{summary.linkCount} 启用</span>
              </div>
              <div className="link-stat-list">
                {summary.links.map((link) => (
                  <button
                    className={selectedLinkId === link.id ? "active" : ""}
                    key={link.id}
                    type="button"
                    onClick={() => setSelectedLinkId(link.id)}
                  >
                    <strong>{link.title || `链接 ${link.id}`}</strong>
                    <span>{link.studentCount} 人 · {link.runCount} 次</span>
                  </button>
                ))}
              </div>
            </article>
          </div>

          <div className="dashboard-student-layout" ref={studentDetailRef}>
            <StudentDetailCard detail={studentDetail} />
          </div>
        </>
      ) : loadState === "loading" ? (
        <article className="dashboard-card">
          <p className="dashboard-empty">正在读取教师看板...</p>
        </article>
      ) : null}
    </section>
  );
}
